/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { FiChevronUp, FiCheck } from "react-icons/fi";
import "./LanguageSelector.css";

// High quality country flags via FlagCDN (Works 100% across all OS & devices)
const languages = [
  { code: "en", name: "English", flagCode: "gb" },
  { code: "gu", name: "Gujarati", flagCode: "in" },
  { code: "hi", name: "Hindi", flagCode: "in" },
  { code: "ar", name: "Arabic", flagCode: "ae" },
  { code: "zh-CN", name: "Chinese (Simplified)", flagCode: "cn" },
  { code: "nl", name: "Dutch", flagCode: "nl" },
  { code: "fr", name: "French", flagCode: "fr" },
  { code: "de", name: "German", flagCode: "de" },
  { code: "it", name: "Italian", flagCode: "it" },
  { code: "pt", name: "Portuguese", flagCode: "pt" },
  { code: "ru", name: "Russian", flagCode: "ru" },
  { code: "es", name: "Spanish", flagCode: "es" },
];

const LanguageSelector = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [scriptError, setScriptError] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef(null);

  const getInitialLang = () => {
    try {
      const cookies = document.cookie.split(";");
      for (let c of cookies) {
        const trimmed = c.trim();
        if (trimmed.startsWith("googtrans=")) {
          const val = trimmed.substring("googtrans=".length);
          const parts = val.split("/");
          if (parts.length >= 3 && parts[2]) {
            return parts[2];
          }
        }
      }
    } catch (e) {
      // Ignore cookie parse error
    }
    return localStorage.getItem("selected_language") || "en";
  };

  const [selectedLang, setSelectedLang] = useState(getInitialLang);

  // Initialize Google Translate Script safely with error recovery
  useEffect(() => {
    const addGoogleTranslateScript = () => {
      if (document.getElementById("google-translate-script")) {
        setScriptLoaded(true);
        return;
      }

      window.googleTranslateElementInit = () => {
        if (window.google && window.google.translate) {
          try {
            new window.google.translate.TranslateElement(
              {
                pageLanguage: "en",
                includedLanguages: "en,gu,hi,ar,zh-CN,nl,fr,de,it,pt,ru,es",
                autoDisplay: false,
              },
              "google_translate_element",
            );
            setScriptLoaded(true);
          } catch (err) {
            console.warn("Google Translate init warning:", err);
          }
        }
      };

      const script = document.createElement("script");
      script.id = "google-translate-script";
      script.src =
        "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
      script.async = true;
      script.onerror = () => {
        console.warn(
          "Google Translate script failed to load (possibly blocked by AdBlocker/Firewall).",
        );
        setScriptError(true);
      };
      document.body.appendChild(script);
    };

    addGoogleTranslateScript();
  }, []);

  // Dispatch change to hidden Google Translate select box
  const applyTranslation = useCallback((langCode) => {
    const selectElem = document.querySelector(".goog-te-combo");
    if (selectElem) {
      if (selectElem.value !== langCode) {
        selectElem.value = langCode;
        selectElem.dispatchEvent(new Event("change", { bubbles: true }));
        selectElem.dispatchEvent(new Event("input", { bubbles: true }));
      }
      return true;
    }
    return false;
  }, []);

  // Re-trigger translation when React Router navigates to a new page/view
  useEffect(() => {
    if (selectedLang && selectedLang !== "en") {
      const timer = setTimeout(() => {
        applyTranslation(selectedLang);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [location.pathname, location.hash, selectedLang, applyTranslation]);

  // Close dropdown on outside click or Escape key
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // Change Language Handler - Instant reload-free switch
  const handleLanguageChange = (langCode) => {
    setSelectedLang(langCode);
    localStorage.setItem("selected_language", langCode);
    setIsOpen(false);

    // Set Google Translate Cookie for root path and current domain
    const hostname = window.location.hostname;
    document.cookie = `googtrans=/en/${langCode}; path=/;`;
    document.cookie = `googtrans=/en/${langCode}; path=/; domain=${hostname}`;
    if (hostname !== "localhost" && hostname.includes(".")) {
      document.cookie = `googtrans=/en/${langCode}; path=/; domain=.${hostname}`;
    }

    // Apply translation dynamically without hard page reload
    const applied = applyTranslation(langCode);

    // Fallback hard reload ONLY if Google Translate DOM element is not ready yet
    if (!applied && langCode !== "en") {
      setTimeout(() => {
        const retry = applyTranslation(langCode);
        if (!retry) {
          window.location.reload();
        }
      }, 200);
    }
  };

  const currentLanguageObj =
    languages.find((l) => l.code === selectedLang) || languages[0];

  return (
    <div
      className="global-language-selector-wrapper notranslate"
      ref={dropdownRef}
    >
      {/* Hidden Container for Google Translate Element */}
      <div id="google_translate_element" style={{ display: "none" }} />

      {/* Floating Selector Pill Button (Fixed Bottom-Left) */}
      <button
        type="button"
        className={`lang-selector-pill ${isOpen ? "active" : ""}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Select Language"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        title="Select Language"
      >
        <img
          src={`https://flagcdn.com/w40/${currentLanguageObj.flagCode}.png`}
          alt={currentLanguageObj.name}
          className="lang-flag-img"
          loading="lazy"
        />
        <span className="lang-code-text">{currentLanguageObj.name}</span>
        <FiChevronUp className={`lang-chevron ${isOpen ? "open" : ""}`} />
      </button>

      {/* Language Popup Menu (Opens Upward) */}
      {isOpen && (
        <div className="lang-dropdown-menu" role="listbox" tabIndex={-1}>
          <div className="lang-options-list">
            {languages.map((lang) => {
              const isSelected = lang.code === selectedLang;
              return (
                <button
                  key={lang.code}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={`lang-option-item ${isSelected ? "selected" : ""}`}
                  onClick={() => handleLanguageChange(lang.code)}
                >
                  <img
                    src={`https://flagcdn.com/w40/${lang.flagCode}.png`}
                    alt={lang.name}
                    className="option-flag-img"
                    loading="lazy"
                  />
                  <span className="option-name">{lang.name}</span>
                  {isSelected && <FiCheck className="option-check" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
